'use client';

import { useRef, useState } from 'react';
import { Button } from '@savvy-kit/components/button';
import InputField from '@savvy-kit/patterns/input-field';
import { isNil } from 'es-toolkit';
import { Download, SearchIcon, Settings2 } from 'lucide-react';
import { graphql } from 'src/gql';

import type { ResultCountConfig } from '@savvy-kit/templates/data-table-filter';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import type { SavedAccountFilter } from './hooks/use-saved-account-filters';
import type { AccountsTableColumn } from './utils/accounts-columns';
import { AccountsColumnsDisplay } from './accounts-columns-display';
import { AccountsFilters } from './filters/accounts-filters';
import { useAccountsParams } from './utils/accounts-url-state';

const AccountsTableHeaderQuery = graphql(/* GraphQL */ `
  query AccountsTableHeader {
    advisor {
      accountFilterOptions {
        ...AccountsFilters
      }
    }
  }
`);

export function AccountsTableHeader({
  selectedColumns,
  onColumnsChange,
  onExport,
  savedFilterProps,
  resultCount,
}: {
  readonly selectedColumns: AccountsTableColumn[];
  readonly onColumnsChange: (columns: AccountsTableColumn[]) => void;
  readonly onExport?: () => void;
  readonly savedFilterProps?: {
    readonly atLimit: boolean;
    readonly activePresetName: string | null;
    readonly isModified: boolean;
    readonly hasFilters: boolean;
    readonly existingNames: readonly string[];
    readonly savedFilters: readonly SavedAccountFilter[];
    readonly onSave: (name: string) => void;
    readonly onUpdate: (id: string) => void;
  };
  readonly resultCount?: ResultCountConfig;
}) {
  const { params, setSearch } = useAccountsParams();
  const [displayColumnsDialogOpen, setDisplayColumnsDialogOpen] = useState(false);
  const displayButtonRef = useRef<HTMLButtonElement>(null);

  const { data } = useQueryWithErrorLogging(AccountsTableHeaderQuery);
  const accountFilterOptions = data?.advisor.accountFilterOptions;

  return (
    <>
      <AccountsColumnsDisplay
        open={displayColumnsDialogOpen}
        onClose={() => setDisplayColumnsDialogOpen(false)}
        selectedColumns={selectedColumns}
        onColumnsChange={onColumnsChange}
        anchorEl={displayButtonRef.current}
      />
      <div className="flex flex-col gap-4 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-1 gap-2 min-w-0">
            <InputField
              id="accounts-search"
              placeholder="Search accounts..."
              aria-label="Search"
              value={params.search ?? ''}
              onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => setSearch(target.value)}
              prependInputAddon={<SearchIcon />}
              className="max-w-xs min-w-[300px]"
            />
            <AccountsFilters
              accountFilterOptionsFrag={accountFilterOptions}
              savedFilterProps={savedFilterProps}
              resultCount={resultCount}
            />
          </div>
          <div className="flex gap-3 items-center">
            {!isNil(onExport) && (
              <Button variant="secondary" icon={<Download size={16} />} onClick={onExport}>
                Export
              </Button>
            )}
            <Button
              ref={displayButtonRef}
              variant="secondary"
              icon={<Settings2 size={20} />}
              onClick={() => setDisplayColumnsDialogOpen(true)}
            >
              Display
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
